import React, { useState, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
	createSession,
	readValues,
	keepSessionAlive,
} from "../functions/excelFunctions";
import Heatmap from "../images/heatmap.svg";
import { Typography } from "@mui/material";
import "../css/Benchmarking.css";
import {
	percentageSorting,
	floatStringSorting,
} from "../functions/sortingFunctions";

function Benchmarking() {
	const [sessionInfo, setSessionInfo] = useState(null);
	const [tableHeaders, setTableHeaders] = useState([]);
	const [tableRows, setTableRows] = useState([]);
	const [datagridColumns, setDatagridColumns] = useState([]);
	const [datagridRows, setDatagridRows] = useState([]);
	const sheet = "Benchmarking";
	const functionCallDelay = 300; //ms

	const getData = useCallback(() => {
		if (sessionInfo) {
			setTimeout(() => {
				readValues(sessionInfo, setTableHeaders, sheet, "B13:E13");
			}, 1 * functionCallDelay);

			setTimeout(() => {
				readValues(sessionInfo, setTableRows, sheet, "B14:E35");
			}, 2 * functionCallDelay);
		}
	}, [sessionInfo, setTableHeaders, setTableRows]);

	useEffect(() => {
		createSession(setSessionInfo);
	}, []);

	useEffect(() => {
		getData();
	}, [getData]);

	useEffect(() => {
		tableHeaders.length &&
			tableRows.length &&
			setDatagridColumns([
				{
					field: "columnA",
					headerName: "Local Authority",
					minWidth: 150,
					flex: 1,
				},
				{
					field: "columnB",
					headerName: "Actual number of CLA",
					headerAlign: "center",
					align: "center",
					sortComparator: floatStringSorting,
					width: 210,
				},
				{
					field: "columnC",
					headerName: "Predicted number of CLA",
					headerAlign: "center",
					align: "center",
					sortComparator: floatStringSorting,
					width: 210,
				},
				{
					field: "columnD",
					headerName: "% difference",
					headerAlign: "center",
					align: "center",
					sortComparator: percentageSorting,
					width: 150,
				},
			]);

		const currentDatagridRows = [];
		tableRows.forEach((row, index) => {
			currentDatagridRows.push({
				id: index,
				columnA: row[0],
				columnB: row[1],
				columnC: row[2],
				columnD: row[3],
			});
		});
		setDatagridRows(currentDatagridRows);
	}, [tableHeaders, tableRows]);
	setInterval(() => {
		sessionInfo && keepSessionAlive(sessionInfo, sheet);
	}, 5 * 60 * 1000); // Send the request every 5 minutes

	return (
		<div className="page-content" id="benchmarking-body">
			<Typography variant="h4" style={{ marginBottom: 30 }}>
				Local Authorities' Benchmarking
			</Typography>
			<Typography variant="body1" style={{ marginBottom: 30 }}>
				This page presents statistics on the number of children looked
				after in 2021 ("Actual number of CLA") compared with the
				predicted number of CLA based on variation in risk factors
				across local authorities using 2021 data ("Predicted number of
				CLA"). The heatmap indicates which local authorities have higher
				or lower totals of CLA compared to the number predicted based on
				underlying risk factors.
			</Typography>
			{datagridColumns.length && datagridRows.length ? (
				<div className="outputs-data-container">
					<div className="table-container">
						<DataGrid
							rows={datagridRows}
							columns={datagridColumns}
							disableRowSelectionOnClick
							disableColumnMenu
							disableColumnSelector
							disableVirtualization
							hideFooter={true}
							autoHeight={true}
							density={"compact"}
						/>

						<div className="table-footer-text">
							The predicted number of CLA is based on risk factors
							measured at Local Authority level including domestic
							abuse, parental substance misuse, parentals mental
							health illness, and deprivation.
						</div>
					</div>
					<div className="chart-container">
						<img src={Heatmap} alt="Heatmap" />
						<div className="table-footer-text">
							Green indicates Local Authorities in which the
							number of CLA is higher than predicted, based on the
							prevalence of risk factors. Orange indicates Local
							Authorities in which the number of CLA is lower than
							predicted.
						</div>
					</div>
				</div>
			) : (
				<div className="loader" />
			)}
		</div>
	);
}

export default Benchmarking;
